// ============================================================================
// remote/contractVideo.ts — ВИДЕО-СЛОЙ нашего wire-контракта v2 (SRP):
// формат видео-пакета (22Б-заголовок), ОБРАТНАЯ сборка кадра (FEC v1 — XOR-
// паритет), H.264 Annex-B и декодер WebCodecs `VideoDecoder`. Байты пакета
// ИДЕНТИЧНЫ датаграмме движка (`desktop/remote-host/src/transport.rs`), зеркало
// которых — `RemoteVideoPacket.kt`. Транспорт (WebSocket) — в contractViewer.ts.
// ============================================================================

// --- Формат видео-пакета (зеркало RemoteVideoPacket.kt / transport.rs) ------

export const REMOTE_VIDEO = {
  PACKET_MAGIC: 0x48,
  PACKET_HEADER_LEN: 22,
  CHUNK_PAYLOAD: 1200,
  FLAG_KEYFRAME: 0x01,
  FLAG_PARITY: 0x02,
  FLAG_FEC_FRAME: 0x04,
} as const;

export interface RemoteVideoPacket {
  seq: number;
  chunkIdx: number;
  chunkCount: number;
  ptsMs: number;
  keyframe: boolean;
  flags: number;
  data: Uint8Array;
}

export function isParityPacket(p: RemoteVideoPacket): boolean {
  return (p.flags & REMOTE_VIDEO.FLAG_PARITY) !== 0;
}
export function isFecFrame(p: RemoteVideoPacket): boolean {
  return (p.flags & REMOTE_VIDEO.FLAG_FEC_FRAME) !== 0;
}

/** Разбор одного бинарного WS-фрейма (22-байтный заголовок + payload). */
export function decodeVideoPacket(buf: Uint8Array): RemoteVideoPacket | null {
  if (buf.byteLength < REMOTE_VIDEO.PACKET_HEADER_LEN) return null;
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  if (dv.getUint8(0) !== REMOTE_VIDEO.PACKET_MAGIC) return null;
  const flags = dv.getUint8(1);
  const seq = dv.getUint32(2, true);
  const chunkIdx = dv.getUint16(6, true);
  const chunkCount = dv.getUint16(8, true);
  const ptsMs = Number(dv.getBigUint64(10, true));
  const len = dv.getUint32(18, true);
  if (buf.byteLength < REMOTE_VIDEO.PACKET_HEADER_LEN + len) return null;
  return {
    seq,
    chunkIdx,
    chunkCount,
    ptsMs,
    keyframe: (flags & REMOTE_VIDEO.FLAG_KEYFRAME) !== 0,
    flags,
    data: buf.subarray(REMOTE_VIDEO.PACKET_HEADER_LEN, REMOTE_VIDEO.PACKET_HEADER_LEN + len),
  };
}

/** XOR-восстановление ОДНОГО потерянного data-чанка (зеркало recover_chunk). */
function recoverChunk(dataChunks: RemoteVideoPacket[], parity: RemoteVideoPacket, dataCount: number): Uint8Array {
  if (parity.data.byteLength < 2) throw new Error('FEC: паритет повреждён');
  const lastLen = parity.data[0] | (parity.data[1] << 8);
  const have = new Array<boolean>(dataCount).fill(false);
  for (const c of dataChunks) if (c.chunkIdx < dataCount) have[c.chunkIdx] = true;
  const missing = have.findIndex((h) => !h);
  if (missing < 0) throw new Error('FEC: потерь нет');
  const acc = new Uint8Array(REMOTE_VIDEO.CHUNK_PAYLOAD);
  for (let i = 0; i < REMOTE_VIDEO.CHUNK_PAYLOAD; i++) acc[i] = parity.data[2 + i];
  for (const c of dataChunks) {
    for (let i = 0; i < c.data.length; i++) acc[i] ^= c.data[i];
  }
  const len = missing === dataCount - 1 ? lastLen : REMOTE_VIDEO.CHUNK_PAYLOAD;
  return acc.subarray(0, len);
}

/** Сборка кадра из чанков (зеркало reassembleFrame); потери > FEC — ошибка. */
export function reassembleFrame(chunks: RemoteVideoPacket[]): Uint8Array {
  if (chunks.length === 0) throw new Error('кадр: нет чанков');
  const total = chunks[0].chunkCount;
  const fec = isFecFrame(chunks[0]);
  const dataCount = fec ? total - 1 : total;
  const dataChunks = chunks.filter((c) => !isParityPacket(c)).sort((a, b) => a.chunkIdx - b.chunkIdx);
  if (dataChunks.length === dataCount) {
    const out = new Uint8Array(dataCount * REMOTE_VIDEO.CHUNK_PAYLOAD);
    let pos = 0;
    dataChunks.forEach((c, idx) => {
      if (c.chunkIdx !== idx) throw new Error(`кадр: пропущен чанк ${idx}`);
      out.set(c.data, pos);
      pos += c.data.length;
    });
    return out.subarray(0, pos);
  }
  if (fec && dataChunks.length + 1 === dataCount) {
    const parity = chunks.find(isParityPacket);
    if (!parity) throw new Error('FEC: нет паритетного чанка');
    const recovered = recoverChunk(dataChunks, parity, dataCount);
    const out = new Uint8Array(dataCount * REMOTE_VIDEO.CHUNK_PAYLOAD);
    let pos = 0;
    let idx = 0;
    for (const c of dataChunks) {
      while (idx < c.chunkIdx) { out.set(recovered, pos); pos += recovered.length; idx++; }
      out.set(c.data, pos);
      pos += c.data.length;
      idx++;
    }
    while (idx < dataCount) { out.set(recovered, pos); pos += recovered.length; idx++; }
    return out.subarray(0, pos);
  }
  throw new Error(`кадр: ${dataChunks.length} data-чанков из ${dataCount} (потери > FEC)`);
}

export interface AssembledVideoFrame {
  seq: number;
  keyframe: boolean;
  ptsMs: number;
  data: Uint8Array;
}

// --- КОДИРОВАНИЕ кадра в видео-пакеты (зеркало packetize движка / хоста) ----
// Используется БРАУЗЕРНЫМ хостом (lib/remote/contractHost.ts): WebCodecs
// VideoEncoder отдаёт Annex-B чанк — он режется по CHUNK_PAYLOAD, заголовок
// 22 байта, при FEC добавляется XOR-паритет (chunkCount = dataCount + 1).
// Байты ИДЕНТИЧНЫ датаграммам движка и пакетам Android-хоста.

function buildVideoPacket(
  seq: number,
  chunkIdx: number,
  chunkCount: number,
  ptsMs: number,
  flags: number,
  data: Uint8Array<ArrayBuffer>,
): Uint8Array<ArrayBuffer> {
  const buf = new Uint8Array(REMOTE_VIDEO.PACKET_HEADER_LEN + data.length);
  const dv = new DataView(buf.buffer);
  dv.setUint8(0, REMOTE_VIDEO.PACKET_MAGIC);
  dv.setUint8(1, flags);
  dv.setUint32(2, seq >>> 0, true);
  dv.setUint16(6, chunkIdx, true);
  dv.setUint16(8, chunkCount, true);
  dv.setBigUint64(10, BigInt(Math.max(0, Math.round(ptsMs))), true);
  dv.setUint32(18, data.length, true);
  buf.set(data, REMOTE_VIDEO.PACKET_HEADER_LEN);
  return buf;
}

/** Кодирование одного H.264-кадра (Annex-B) в видео-пакеты контракта.
 *  fec=true добавляет XOR-паритет (восстановление 1 потерянного чанка). */
export function encodeVideoPackets(
  data: Uint8Array<ArrayBuffer>,
  seq: number,
  ptsMs: number,
  keyframe: boolean,
  fec = true,
): Uint8Array<ArrayBuffer>[] {
  const dataCount = Math.max(1, Math.ceil(data.length / REMOTE_VIDEO.CHUNK_PAYLOAD));
  const chunkCount = fec ? dataCount + 1 : dataCount;
  const flagsBase = (keyframe ? REMOTE_VIDEO.FLAG_KEYFRAME : 0) | (fec ? REMOTE_VIDEO.FLAG_FEC_FRAME : 0);
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  const packets: Uint8Array<ArrayBuffer>[] = [];
  for (let i = 0; i < dataCount; i++) {
    const start = i * REMOTE_VIDEO.CHUNK_PAYLOAD;
    const chunk = data.subarray(start, Math.min(data.length, start + REMOTE_VIDEO.CHUNK_PAYLOAD));
    chunks.push(chunk);
    packets.push(buildVideoPacket(seq, i, chunkCount, ptsMs, flagsBase, chunk));
  }
  if (fec) {
    const parity = new Uint8Array(2 + REMOTE_VIDEO.CHUNK_PAYLOAD);
    const lastLen = chunks[dataCount - 1].length;
    parity[0] = lastLen & 0xff;
    parity[1] = (lastLen >> 8) & 0xff;
    for (let i = 0; i < REMOTE_VIDEO.CHUNK_PAYLOAD; i++) {
      let acc = 0;
      for (const c of chunks) if (i < c.length) acc ^= c[i];
      parity[2 + i] = acc;
    }
    packets.push(buildVideoPacket(seq, dataCount, chunkCount, ptsMs, flagsBase | REMOTE_VIDEO.FLAG_PARITY, parity));
  }
  return packets;
}

/** Накопитель чанков по seq: возвращает готовый кадр (или null, если не полон). */
export class VideoFrameAssembler {
  private frames = new Map<number, { chunks: Map<number, RemoteVideoPacket>; count: number; fec: boolean; keyframe: boolean; ptsMs: number }>();
  private static readonly MAX_PENDING = 32;

  push(p: RemoteVideoPacket): AssembledVideoFrame | null {
    if (p.chunkCount === 0 || p.chunkIdx >= p.chunkCount) return null;
    let fb = this.frames.get(p.seq);
    if (!fb) {
      fb = { chunks: new Map(), count: p.chunkCount, fec: isFecFrame(p), keyframe: p.keyframe, ptsMs: p.ptsMs };
      this.frames.set(p.seq, fb);
      if (this.frames.size > VideoFrameAssembler.MAX_PENDING) {
        const oldest = Math.min(...this.frames.keys());
        if (oldest !== p.seq) this.frames.delete(oldest);
      }
    }
    fb.chunks.set(p.chunkIdx, p);
    const list = Array.from(fb.chunks.values());
    const dataCount = fb.fec ? fb.count - 1 : fb.count;
    const dataPresent = list.filter((c) => !isParityPacket(c)).length;
    const parityPresent = list.some(isParityPacket);
    const complete = (!fb.fec && dataPresent === dataCount)
      || (fb.fec && parityPresent && (dataPresent === dataCount || dataPresent === dataCount - 1));
    if (!complete) return null;
    try {
      const data = reassembleFrame(list);
      this.frames.delete(p.seq);
      return { seq: p.seq, keyframe: fb.keyframe, ptsMs: fb.ptsMs, data };
    } catch {
      this.frames.delete(p.seq);
      return null;
    }
  }

  reset(): void {
    this.frames.clear();
  }
}

// --- H.264 Annex-B / WebCodecs ---------------------------------------------

/** NAL-единицы Annex-B (по стартовым кодам 00 00 01 / 00 00 00 01). */
function splitAnnexBNals(data: Uint8Array): Uint8Array[] {
  const out: Uint8Array[] = [];
  const n = data.length;
  const findStart = (from: number): number => {
    for (let j = from; j + 2 < n; j++) {
      if (data[j] === 0 && data[j + 1] === 0) {
        if (data[j + 2] === 1) return j;
        if (data[j + 2] === 0 && j + 3 < n && data[j + 3] === 1) return j;
      }
    }
    return -1;
  };
  let start = findStart(0);
  while (start >= 0) {
    const scLen = data[start + 2] === 1 ? 3 : 4;
    const nalStart = start + scLen;
    const next = findStart(nalStart);
    const nalEnd = next >= 0 ? next : n;
    if (nalEnd > nalStart) out.push(data.subarray(nalStart, nalEnd));
    start = next;
  }
  return out;
}

const hex2 = (v: number): string => v.toString(16).padStart(2, '0').toLowerCase();

/** RFC 6381 `avc1.PPCCLL` из SPS (profile_idc/constraint_flags/level_idc). */
export function h264CodecString(annexB: Uint8Array): string | null {
  for (const nal of splitAnnexBNals(annexB)) {
    if ((nal[0] & 0x1f) === 7 && nal.length >= 4) {
      return `avc1.${hex2(nal[1])}${hex2(nal[2])}${hex2(nal[3])}`;
    }
  }
  return null;
}

/** Декодер H.264 Annex-B на WebCodecs: csd (SPS/PPS) — в кейфрейме (in-band). */
export class H264AnnexBDecoder {
  private decoder: VideoDecoder | null = null;
  private codec = '';
  constructor(
    private readonly onFrame: (frame: VideoFrame) => void,
    private readonly onError: (message: string) => void,
  ) {}

  get codecString(): string { return this.codec; }

  feed(data: Uint8Array, keyframe: boolean, ptsMs: number): void {
    if (typeof VideoDecoder === 'undefined') {
      this.onError('WebCodecs VideoDecoder недоступен в этом окружении');
      return;
    }
    if (!this.decoder) {
      this.decoder = new VideoDecoder({
        output: (frame) => this.onFrame(frame),
        error: (e) => this.onError(`H.264 декодер: ${e.message}`),
      });
    }
    if (this.decoder.state !== 'configured') {
      if (!keyframe) return; // до первого кейфрейма (SPS/PPS) настраивать нечем
      const codec = h264CodecString(data);
      if (!codec) {
        this.onError('H.264: в кейфрейме нет SPS — декодер не настроен');
        return;
      }
      this.codec = codec;
      const config: VideoDecoderConfig & { avc: { format: 'annexb' } } = {
        codec,
        avc: { format: 'annexb' },
        optimizeForLatency: true,
      };
      try {
        this.decoder.configure(config);
      } catch (e) {
        this.onError(`H.264: не удалось настроить декодер (${codec}): ${String((e as Error)?.message || e)}`);
        return;
      }
    }
    if (this.decoder.state !== 'configured') return;
    try {
      this.decoder.decode(new EncodedVideoChunk({
        type: keyframe ? 'key' : 'delta',
        timestamp: Math.max(0, Math.round(ptsMs * 1000)),
        data,
      }));
    } catch (e) {
      this.onError(`H.264: кадр не принят декодером: ${String((e as Error)?.message || e)}`);
    }
  }

  close(): void {
    if (!this.decoder) return;
    try {
      this.decoder.close();
    } catch {
      /* уже закрыт */
    }
    this.decoder = null;
  }
}
